import React from 'react';
import { Row, Col, List } from 'reactstrap';
import PropTypes from 'prop-types';
import Body from '../Components/layout/Body';
import ReviewRow from '../Components/layout/ReviewRow';
import { TABS, SUBTABS, PHYSICAL_STEPS, VISUAL_STEPS, COGNITION_STEPS, OTHER_STEPS } from './ConstPaths';
import { extractBodySiteButtons } from './ConstBodySiteBtns';
import { VISION_BUTTONS } from './ConstVisionBtns';

//the props should have been picked before getting to review,
//so make them all required (except name, which is always optional)
const propTypes = {
  firstName: PropTypes.string,
  bodySites: PropTypes.arrayOf(PropTypes.string),
  bodySiteComments: PropTypes.string,
  wearsGlasses: PropTypes.string.isRequired,
  betterWithGlasses: PropTypes.string.isRequired,
  hasOtherVisualAids: PropTypes.string.isRequired,
  visualAidsNotes: PropTypes.string.isRequired,
  formalEvalNotes: PropTypes.string.isRequired,
  visionIssues: PropTypes.arrayOf(PropTypes.string).isRequired,
  issuesNotes: PropTypes.string.isRequired,
  strengths: PropTypes.string.isRequired,
  limitations: PropTypes.string.isRequired,
  otherIssues: PropTypes.string.isRequired, 
};

const defaultProps = {
  firstName: 'the client',
};

//content sub-component with unique content for this page
function EvaluateAbilitiesReviewContent(props){
  const firstName = props.firstName;
  const pronouns = props.pronouns;
  //props for physical section
  const bodySites = props.bodySites;
  const bodySiteComments = props.bodySiteComments;
  //props for visual section
  const wearsGlasses = props.wearsGlasses;
  const betterWithGlasses = props.betterWithGlasses;
  const hasOtherVisualAids = props.hasOtherVisualAids;
  const visualAidsNotes = props.visualAidsNotes;
  const formalEvalNotes = props.formalEvalNotes;
  const visionIssues = props.visionIssues;
  const issuesNotes = props.issuesNotes;
  //props for language-cognition section
  const strengths = props.strengths;
  const limitations = props.limitations;
  //props for other characteristics section
  const otherIssues = props.otherIssues;

  let bodySiteBtns = extractBodySiteButtons();

  const PHYSICAL_URL = SUBTABS.PHYSICAL + "/";
  const VISUAL_URL = SUBTABS.VISUAL + "/";
  const COGNITION_URL = SUBTABS.COGNITION + "/";
  const OTHER_URL = SUBTABS.OTHER + "/";

  const THREE_STATE_LABELS = [ 
    {
      value: 'true',
      label: 'yes',
    },
    {
      value: 'false',
      label: 'no'
    },
    {
      value: 'not-sure',
      label: 'not sure'
    }
  ];

  return(
  <>
    <Row>
      <Col>
      <div className="row aa-review-row border-top">
        <div className="col-md-1">
          <span className="fa fa-child fa-3x aa-eval-color"></span>
        </div>
        <div className="col">
          <h5>Physical</h5>
          <div className="col">
            <ReviewRow 
              border="none"
              padded="none"
              heading="Priority body sites" 
              nextRoute={PHYSICAL_URL+PHYSICAL_STEPS.BASIC}
              values={bodySites}
              lookup={bodySiteBtns}
            />
            <ReviewRow 
              border="none"
              padded="none"
              heading="Notes on body sites" 
              nextRoute={PHYSICAL_URL+PHYSICAL_STEPS.INDEPTH}
              values={bodySiteComments}
              lookup={null}
            />
          </div>
        </div>
      </div>
      <div className="row aa-review-row border-top">
        <div className="col-md-1">
          <span className="fa fa-eye fa-2x aa-eval-color"></span>
        </div>
        <div className="col">
          <h5>Visual</h5>
          <div className="col">
            <ReviewRow 
              border="none"
              padded="none"
              heading="Wears glasses" 
              nextRoute={VISUAL_URL+VISUAL_STEPS.BACKGROUND}
              values={wearsGlasses}
              lookup={THREE_STATE_LABELS}
            />
            {(wearsGlasses==='true') ? 
            <ReviewRow 
              border="none"
              padded="none"
              heading="Sees screen better with glasses" 
              nextRoute={VISUAL_URL+VISUAL_STEPS.BACKGROUND}
              values={betterWithGlasses}
              lookup={THREE_STATE_LABELS}
            />
            : <span></span>}
            <ReviewRow 
              border="none"
              padded="none"
              heading="Has other visual aids" 
              nextRoute={VISUAL_URL+VISUAL_STEPS.BACKGROUND}
              values={hasOtherVisualAids}
              lookup={THREE_STATE_LABELS}
            />
           {(hasOtherVisualAids==='true') ? 
            <ReviewRow 
            border="none"
            padded="none"
            heading="List of other visual aids" 
            nextRoute={VISUAL_URL+VISUAL_STEPS.BACKGROUND}
            values={visualAidsNotes}
            lookup={null}
            />
            : <span></span>}
            <ReviewRow 
              border="none"
              padded="none"
              heading="Formal vision eval notes " 
              nextRoute={VISUAL_URL+VISUAL_STEPS.BACKGROUND}
              values={formalEvalNotes}
              lookup={null}
            />
            <ReviewRow 
              border="none"
              padded="none"
              heading="Vision issues" 
              nextRoute={VISUAL_URL+VISUAL_STEPS.ISSUES}
              values={visionIssues}
              lookup={VISION_BUTTONS}
            />
            <ReviewRow 
              border="none"
              padded="none"
              heading="Comments on vision issues" 
              nextRoute={VISUAL_URL+VISUAL_STEPS.ISSUES}
              values={issuesNotes}
              lookup={null}
            />
          </div>
        </div>
      </div>
      <div className="row aa-review-row border-top">
        <div className="col-md-1">
          <span className="fa fa-puzzle-piece fa-2x aa-eval-color"></span>
        </div>
        <div className="col">
          <h5>Language and Cognition</h5>
          <div className="col">
            <ReviewRow 
              border="none"
              padded="none"
              heading="Strengths" 
              nextRoute={COGNITION_URL+COGNITION_STEPS.FEATURES}
              values={strengths}
              lookup={null}
            />
            <ReviewRow 
              border="none"
              padded="none"
              heading="Limitations" 
              nextRoute={COGNITION_URL+COGNITION_STEPS.FEATURES}
              values={limitations}
              lookup={null}
            />
          </div>
        </div>
      </div>
      <div className="row aa-review-row border-top">
        <div className="col-md-1">
          <span className="fa fa-superpowers fa-2x aa-eval-color"></span>
        </div>
        <div className="col">
          <h5>Other Characteristics</h5>
          <div className="col">
            <ReviewRow 
              border="none"
              padded="none"
              heading="Additional notes" 
              nextRoute={OTHER_URL+OTHER_STEPS.CHARACTERISTICS}
              values={otherIssues}
              lookup={null}
            />
          </div>
        </div>
      </div>

      </Col> 
      <Col lg={2}></Col>
    </Row>
    <Row>
      <Col>
      You can Update a section if you want to make changes.  When you're done with all relevant sections, select Continue.
      </Col>
    </Row>
  </>
  );
}

//main component for this page
//basic pattern is to set up the pageData, define the unique content for the page (above),
//then render the page using <Body> component.
//since the main goal step includes a form, receive the initial form values via props,
//along with setters to change those values based on the form input.
function EvaluateAbilitiesReview(props) {
  const firstName = props.firstName;
  const pronouns = props.pronouns;
  //props for physical section
  const bodySites = props.bodySites;
  const bodySiteComments = props.bodySiteComments;
  //props for visual section
  const wearsGlasses = props.wearsGlasses;
  const betterWithGlasses = props.betterWithGlasses;
  const hasOtherVisualAids = props.hasOtherVisualAids;
  const visualAidsNotes = props.visualAidsNotes;
  const formalEvalNotes = props.formalEvalNotes;
  const visionIssues = props.visionIssues;
  const issuesNotes = props.issuesNotes;
  //props for language-cognition section
  const strengths = props.strengths;
  const limitations = props.limitations;
  //props for other characteristics section
  const otherIssues = props.otherIssues;

  console.log("rendering EvaluateAbilitiesReview");

  const pageData = {
    image: "./assets/images/target-icon.jpg", //not yet used
    faIcon: "",
    pageSymbolClassName: "aa-section-symbol eval",
    title: `Review ${firstName}'s Abilities Profile`,
    subTitle: "Great news! We've visited all 4 sections on evaluating abilities.\nHere's what we've got:",
    learnMore: {linkText: "",
                title: "",
                content: ""
                },
    nextRoute: TABS.TEST, //TODO used for now instead of an onContinue callback
    sidebar: false
  }

  return(
    <>
    <Body page={pageData}
      contentComponent={
        <EvaluateAbilitiesReviewContent
          firstName={firstName}
          pronouns={pronouns} 
          bodySites={bodySites}
          bodySiteComments={bodySiteComments}
          wearsGlasses={wearsGlasses}
          betterWithGlasses={betterWithGlasses}
          hasOtherVisualAids={hasOtherVisualAids}
          visualAidsNotes={visualAidsNotes}
          formalEvalNotes={formalEvalNotes}
          visionIssues={visionIssues}
          issuesNotes={issuesNotes}
          strengths={strengths}
          limitations={limitations}
          otherIssues={otherIssues}
        />
      }
    />
    </>
  );
}

EvaluateAbilitiesReview.propTypes = propTypes;
EvaluateAbilitiesReview.defaultProps = defaultProps;

export default EvaluateAbilitiesReview;